import { AnimatePresence, motion } from 'framer-motion';
import {
  Briefcase,
  Check,
  ChevronDown,
  Code,
  Flag,
  Loader2,
  PenTool,
  Plus,
  Shapes,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileService } from '@/api/services/profile';
import { useAuth } from '@/providers/AuthProvider';

// --- Types ---

type Gender = 'masculino' | 'feminino' | 'outro' | 'prefiro-nao-dizer';
type Archetype = 'engineer' | 'product_manager' | 'designer' | 'founder' | 'generalist';
type ThemeStatus = 'following' | 'blocked';
type Theme = {
  id: string;
  name: string;
  status: ThemeStatus;
};
type ProfileData = {
  name: string;
  gender: Gender;
  profileTypes: Archetype[];
  role: string;
  industry: string;
  themes: Theme[];
};

// --- Mock Data ---

const ROLES = [
  'Engenheiro de Software',
  'Engenheiro Frontend',
  'Engenheiro Backend',
  'Product Manager',
  'Product Designer',
  'UX Designer',
  'Founder / CEO',
  'CTO',
  'Head of Engineering',
  'Head of Product',
  'Head of Design',
];
const INDUSTRIES = [
  'Tecnologia / SaaS',
  'Fintech',
  'E-commerce',
  'Healthtech',
  'Edtech',
  'Marketplace',
  'Enterprise Software',
  'Consumer Apps',
  'Infrastructure',
  'DevTools',
];
const INITIAL_THEMES: Theme[] = [
  { id: '1', name: 'AI / Machine Learning', status: 'following' },
  { id: '2', name: 'Produto', status: 'following' },
  { id: '3', name: 'Startups', status: 'following' },
  { id: '4', name: 'Engenharia', status: 'following' },
  { id: '5', name: 'Design', status: 'following' },
  { id: '6', name: 'Blockchain / Web3', status: 'blocked' },
];

// --- Helper Components ---

function ArchetypeIcon({ type, className = '' }: { type: Archetype; className?: string }) {
  const iconProps = { size: 24, className };
  switch (type) {
    case 'engineer':
      return <Code {...iconProps} />;
    case 'product_manager':
      return <Briefcase {...iconProps} />;
    case 'designer':
      return <PenTool {...iconProps} />;
    case 'founder':
      return <Flag {...iconProps} />;
    case 'generalist':
      return <Shapes {...iconProps} />;
    default:
      return <Shapes {...iconProps} />;
  }
}

function ArchetypeCard({
  type,
  label,
  isSelected,
  onClick,
}: {
  type: Archetype;
  label: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`
        relative w-full p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer
        flex flex-col items-center justify-center gap-3 min-h-[120px]
        ${isSelected ? 'bg-[#FDEAE3] border-[#E94E1B]' : 'bg-white border-[#CAC7C7] hover:border-[#888185]'}
      `}
    >
      <AnimatePresence>
        {isSelected && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="absolute top-2 right-2 w-6 h-6 bg-[#E94E1B] rounded-full flex items-center justify-center"
          >
            <Check size={14} className="text-white" strokeWidth={3} />
          </motion.div>
        )}
      </AnimatePresence>
      <ArchetypeIcon type={type} className={isSelected ? 'text-[#E94E1B]' : 'text-[#888185]'} />
      <span className={`text-sm font-medium ${isSelected ? 'text-[#E94E1B]' : 'text-[#2E2A23]'}`}>
        {label}
      </span>
    </motion.button>
  );
}

function Dropdown({
  value,
  options,
  placeholder,
  onChange,
}: {
  value: string;
  options: string[];
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-white border border-[#CAC7C7] rounded-lg text-left flex items-center justify-between hover:border-[#888185] transition-colors cursor-pointer"
      >
        <span className={value ? 'text-[#2E2A23]' : 'text-[#888185]'}>{value || placeholder}</span>
        <ChevronDown
          size={18}
          className={`text-[#888185] transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <>
            <button
              type="button"
              aria-label="Fechar menu"
              className="fixed inset-0 z-10 cursor-default"
              onClick={() => setIsOpen(false)}
              onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full mt-2 w-full bg-white border border-[#CAC7C7] rounded-lg shadow-lg max-h-[200px] overflow-y-auto z-20"
            >
              {options.map((option) => (
                <button
                  type="button"
                  key={option}
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-[#F4F2F2] text-[#2E2A23] text-sm transition-colors cursor-pointer"
                >
                  {option}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function ThemeTag({
  theme,
  onToggleStatus,
  onRemove,
}: {
  theme: Theme;
  onToggleStatus: () => void;
  onRemove: () => void;
}) {
  const isFollowing = theme.status === 'following';
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`
        inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors
        ${isFollowing ? 'bg-[#FDEAE3] text-[#E94E1B] border border-[#E94E1B]/20' : 'bg-[#C45A5A]/10 text-[#C45A5A] border border-[#C45A5A]/20'}
      `}
    >
      <button
        type="button"
        onClick={onToggleStatus}
        className="hover:opacity-70 transition-opacity cursor-pointer"
      >
        {theme.name}
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="hover:opacity-70 transition-opacity cursor-pointer"
      >
        <X size={14} />
      </button>
    </motion.div>
  );
}

function BlockThemeDropdown({
  onAddTheme,
  existingThemes,
}: {
  onAddTheme: (themeName: string) => void;
  existingThemes: string[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const availableThemes = [
    'Blockchain / Web3',
    'Criptomoedas',
    'NFTs',
    'Marketing Digital',
    'Growth Hacking',
    'Vendas',
    'DevOps',
    'Mobile',
    'Backend',
    'Frontend',
    'Data Science',
    'Cloud',
  ].filter((theme) => !existingThemes.includes(theme));

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-[#888185] hover:bg-[#F4F2F2] transition-colors cursor-pointer"
      >
        <Plus size={16} />
        Bloquear tema
      </button>
      <AnimatePresence>
        {isOpen && (
          <>
            <button
              type="button"
              aria-label="Fechar menu"
              className="fixed inset-0 z-10 cursor-default"
              onClick={() => setIsOpen(false)}
              onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full mt-2 left-0 bg-white border border-[#CAC7C7] rounded-lg shadow-lg min-w-[200px] max-h-[200px] overflow-y-auto z-20"
            >
              {availableThemes.map((theme) => (
                <button
                  type="button"
                  key={theme}
                  onClick={() => {
                    onAddTheme(theme);
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-[#F4F2F2] text-[#2E2A23] text-sm transition-colors cursor-pointer"
                >
                  {theme}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// Main Profile Component
export function ProfilePage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const [profileId, setProfileId] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileData>({
    name: '',
    gender: 'prefiro-nao-dizer',
    profileTypes: [],
    role: '',
    industry: '',
    themes: INITIAL_THEMES,
  });
  const [originalProfile, setOriginalProfile] = useState<ProfileData | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(true);

  // Carregar dados do perfil na montagem
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const apiProfile = await profileService.getProfile();
        if (apiProfile) {
          setProfileId(apiProfile.id);
          const prefs = (apiProfile.preferences || {}) as Record<string, unknown>;

          // Converter tags bloqueadas para o formato Theme
          const blockedTags = ((prefs.blocked as Record<string, unknown>)?.tags as string[]) || [];
          const explicitTags = (prefs.explicit_tags as string[]) || [];

          const themesFromApi: Theme[] = [
            ...explicitTags.map((tag, i) => ({
              id: `f-${i}`,
              name: tag,
              status: 'following' as ThemeStatus,
            })),
            ...blockedTags.map((tag, i) => ({
              id: `b-${i}`,
              name: tag,
              status: 'blocked' as ThemeStatus,
            })),
          ];

          const loadedProfile: ProfileData = {
            name: apiProfile.name || user?.email?.split('@')[0] || '',
            gender: (prefs.gender as Gender) || 'prefiro-nao-dizer',
            profileTypes: (prefs.profileTypes as Archetype[]) || [],
            role: apiProfile.title || '', // Job title from user table
            industry: apiProfile.industry || '', // Industry from user table
            themes: themesFromApi.length > 0 ? themesFromApi : INITIAL_THEMES,
          };

          setProfile(loadedProfile);
          setOriginalProfile(loadedProfile);
        } else {
          // Perfil não existe, usar valores padrão
          const defaultProfile: ProfileData = {
            name: user?.email?.split('@')[0] || '',
            gender: 'prefiro-nao-dizer',
            profileTypes: [],
            role: '',
            industry: '',
            themes: INITIAL_THEMES,
          };
          setProfile(defaultProfile);
          setOriginalProfile(defaultProfile);
        }
      } catch (error) {
        console.error('Erro ao carregar perfil:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [user?.email]);

  const isDirty = originalProfile
    ? JSON.stringify(profile) !== JSON.stringify(originalProfile)
    : false;

  const handleSave = async () => {
    if (!profileId) return;

    setSaveState('saving');
    try {
      // Separar temas seguidos e bloqueados
      const followingThemes = profile.themes
        .filter((t) => t.status === 'following')
        .map((t) => t.name);
      const blockedThemes = profile.themes.filter((t) => t.status === 'blocked').map((t) => t.name);

      await profileService.updateProfile(profileId, {
        userName: profile.name,
        gender: profile.gender,
        profileTypes: profile.profileTypes,
        role: profile.role,
        industry: profile.industry,
        explicit_tags: followingThemes,
        blocked: { tags: blockedThemes },
      });

      setOriginalProfile(profile);
      setSaveState('success');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch (error) {
      console.error('Erro ao salvar perfil:', error);
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/feed');
  };

  const toggleArchetype = (archetype: Archetype) => {
    setProfile((prev) => ({
      ...prev,
      profileTypes: prev.profileTypes.includes(archetype)
        ? prev.profileTypes.filter((a) => a !== archetype)
        : [...prev.profileTypes, archetype],
    }));
  };

  const toggleThemeStatus = (themeId: string) => {
    setProfile((prev) => ({
      ...prev,
      themes: prev.themes.map((t) =>
        t.id === themeId ? { ...t, status: t.status === 'following' ? 'blocked' : 'following' } : t,
      ),
    }));
  };

  const removeTheme = (themeId: string) => {
    setProfile((prev) => ({
      ...prev,
      themes: prev.themes.filter((t) => t.id !== themeId),
    }));
  };

  const addBlockedTheme = (themeName: string) => {
    const newTheme: Theme = {
      id: Date.now().toString(),
      name: themeName,
      status: 'blocked',
    };
    setProfile((prev) => ({
      ...prev,
      themes: [...prev.themes, newTheme],
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F4F2F2] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#E94E1B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F2F2] text-[#2E2A23] pb-32">
      {/* Header */}
      <header className="w-full border-b border-[#CAC7C7] bg-white sticky top-0 z-40">
        <div className="max-w-[600px] mx-auto px-6 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/feed')}
            className="text-[#888185] hover:text-[#2E2A23] transition-colors text-sm font-medium cursor-pointer"
          >
            ← Voltar
          </button>
          <h1 className="font-serif text-lg font-semibold">Meu Perfil</h1>
          <div className="w-16" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[600px] mx-auto px-6 py-8 space-y-6">
        {/* Page Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2E2A23] mb-2">
            Meu Perfil
          </h2>
          <p className="text-[#888185] text-base">Personalize sua experiência na Newsletter</p>
        </motion.div>

        {/* Sobre Você */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 border border-[#CAC7C7]"
        >
          <h3 className="font-serif text-xl font-semibold mb-4">Sobre Você</h3>

          <div className="mb-4">
            <label htmlFor="profile-name" className="block text-sm font-medium text-[#888185] mb-2">
              Nome
            </label>
            <input
              id="profile-name"
              type="text"
              value={profile.name}
              onChange={(e) => setProfile((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Seu nome"
              className="w-full px-4 py-3 bg-white border border-[#CAC7C7] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E94E1B]/20 focus:border-[#E94E1B] transition-all"
            />
          </div>

          <div>
            <span className="block text-sm font-medium text-[#888185] mb-2">Gênero</span>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'masculino', label: 'Masculino' },
                { value: 'feminino', label: 'Feminino' },
                { value: 'outro', label: 'Outro' },
                { value: 'prefiro-nao-dizer', label: 'Prefiro não dizer' },
              ].map((option) => (
                <button
                  type="button"
                  key={option.value}
                  onClick={() =>
                    setProfile((prev) => ({ ...prev, gender: option.value as Gender }))
                  }
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer
                    ${
                      profile.gender === option.value
                        ? 'bg-[#E94E1B] text-white'
                        : 'bg-[#F4F2F2] text-[#888185] hover:bg-[#CAC7C7]'
                    }
                  `}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {!profile.name.trim() && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 p-4 bg-[#FDEAE3] rounded-lg border border-[#E94E1B]/20"
            >
              <p className="text-sm text-[#E94E1B]">
                Adicione seu nome para personalizar sua experiência
              </p>
            </motion.div>
          )}
        </motion.section>

        {/* Seu Perfil (Archetypes) */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 border border-[#CAC7C7]"
        >
          <h3 className="font-serif text-xl font-semibold mb-2">Seu Perfil</h3>
          <p className="text-sm text-[#888185] mb-4">
            Selecione um ou mais perfis que te representam
          </p>

          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <div>
              <ArchetypeCard
                type="engineer"
                label="Engenheiro"
                isSelected={profile.profileTypes.includes('engineer')}
                onClick={() => toggleArchetype('engineer')}
              />
            </div>
            <div>
              <ArchetypeCard
                type="product_manager"
                label="PM"
                isSelected={profile.profileTypes.includes('product_manager')}
                onClick={() => toggleArchetype('product_manager')}
              />
            </div>
            <div>
              <ArchetypeCard
                type="designer"
                label="Designer"
                isSelected={profile.profileTypes.includes('designer')}
                onClick={() => toggleArchetype('designer')}
              />
            </div>
            <div>
              <ArchetypeCard
                type="founder"
                label="Founder"
                isSelected={profile.profileTypes.includes('founder')}
                onClick={() => toggleArchetype('founder')}
              />
            </div>
            <div className="md:col-span-2 md:w-[calc(50%-8px)] md:mx-auto">
              <ArchetypeCard
                type="generalist"
                label="Generalista"
                isSelected={profile.profileTypes.includes('generalist')}
                onClick={() => toggleArchetype('generalist')}
              />
            </div>
          </div>
        </motion.section>

        {/* Contexto Profissional */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 border border-[#CAC7C7]"
        >
          <h3 className="font-serif text-xl font-semibold mb-4">Contexto Profissional</h3>

          <div className="space-y-4">
            <div>
              <span className="block text-sm font-medium text-[#888185] mb-2">Cargo</span>
              <Dropdown
                value={profile.role}
                options={ROLES}
                placeholder="Selecione seu cargo"
                onChange={(value) => setProfile((prev) => ({ ...prev, role: value }))}
              />
            </div>

            <div>
              <span className="block text-sm font-medium text-[#888185] mb-2">Indústria</span>
              <Dropdown
                value={profile.industry}
                options={INDUSTRIES}
                placeholder="Selecione sua indústria"
                onChange={(value) => setProfile((prev) => ({ ...prev, industry: value }))}
              />
            </div>
          </div>
        </motion.section>

        {/* Seus Interesses */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 border border-[#CAC7C7]"
        >
          <h3 className="font-serif text-xl font-semibold mb-2">Seus Interesses</h3>
          <p className="text-sm text-[#888185] mb-4">
            Gerencie os temas que você segue ou bloqueia
          </p>

          <div className="flex flex-wrap gap-2 mb-4">
            <AnimatePresence mode="popLayout">
              {profile.themes.map((theme) => (
                <ThemeTag
                  key={theme.id}
                  theme={theme}
                  onToggleStatus={() => toggleThemeStatus(theme.id)}
                  onRemove={() => removeTheme(theme.id)}
                />
              ))}
            </AnimatePresence>
          </div>

          <BlockThemeDropdown
            onAddTheme={addBlockedTheme}
            existingThemes={profile.themes.map((t) => t.name)}
          />
        </motion.section>

        {/* Conta */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-6 border border-[#CAC7C7]"
        >
          <h3 className="font-serif text-xl font-semibold mb-4">Conta</h3>

          <div className="space-y-3 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#888185]">Email</span>
              <span className="text-sm font-medium">{user?.email || '-'}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleSignOut}
            className="w-full py-3 px-4 bg-[#F4F2F2] hover:bg-[#CAC7C7] text-[#C45A5A] rounded-lg font-medium transition-colors text-sm cursor-pointer"
          >
            Sair da conta
          </button>
        </motion.section>
      </main>

      {/* Sticky Save Button */}
      <AnimatePresence>
        {isDirty && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#CAC7C7] p-4 z-50"
          >
            <div className="max-w-[600px] mx-auto">
              <button
                type="button"
                onClick={handleSave}
                disabled={saveState !== 'idle'}
                className={`
                  w-full py-4 px-6 rounded-xl font-semibold text-base transition-all flex items-center justify-center gap-2 cursor-pointer
                  ${
                    saveState === 'idle'
                      ? 'bg-[#2E2A23] hover:bg-[#1A1816] text-white shadow-lg'
                      : saveState === 'saving'
                        ? 'bg-[#888185] text-white cursor-wait'
                        : saveState === 'success'
                          ? 'bg-[#5C8A6E] text-white'
                          : 'bg-[#C45A5A] text-white'
                  }
                `}
              >
                {saveState === 'idle' && 'Salvar alterações'}
                {saveState === 'saving' && (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Salvando...
                  </>
                )}
                {saveState === 'success' && (
                  <>
                    <Check size={18} />
                    Salvo!
                  </>
                )}
                {saveState === 'error' && (
                  <>
                    <X size={18} />
                    Erro ao salvar
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
